import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import { injectIntl, intlShape } from '../../../util/reactIntl';
import css from './AiPromptSection.module.css';
import shape1 from '../../../assets/landing/what-ask-shape-1.png';
import shape2 from '../../../assets/landing/what-ask-shape-2.png';
import shape3 from '../../../assets/landing/what-ask-shape-3.png';
import Icons from '../../../components/Icons/Icons';
import waveImg from '../../../assets/landing/ask-wave.svg';
import classNames from 'classnames';

const getNearestScrollParent = element => {
  if (!element || typeof window === 'undefined') return window;

  let parent = element.parentElement;
  while (parent) {
    const styles = window.getComputedStyle(parent);
    const overflowY = styles.overflowY;
    const canScroll = /(auto|scroll|overlay)/.test(overflowY);
    if (canScroll && parent.scrollHeight > parent.clientHeight) {
      return parent;
    }
    parent = parent.parentElement;
  }

  return window;
};

export const AiPromptSection = props => {
  const { intl, className } = props;
  const history = useHistory();

  const [prompt, setPrompt] = useState('');
  const [isInputHighlighted, setIsInputHighlighted] = useState(false);
  const inputRef = useRef(null);
  const inputRowRef = useRef(null);
  const focusTimeoutRef = useRef(null);
  const highlightTimeoutRef = useRef(null);

  const focusPromptInput = useCallback(() => {
    const input = inputRef.current;
    if (!input) return;

    try {
      input.focus({ preventScroll: true });
    } catch (e) {
      input.focus();
    }
    const textLength = input.value.length;
    if (typeof input.setSelectionRange === 'function') {
      input.setSelectionRange(textLength, textLength);
    }
  }, []);

  const scrollToPromptInput = useCallback(() => {
    if (typeof window === 'undefined') return;
    const inputRow = inputRowRef.current;
    if (!inputRow) return;

    const scrollParent = getNearestScrollParent(inputRow);

    if (scrollParent && scrollParent !== window) {
      const parentRect = scrollParent.getBoundingClientRect();
      const targetRect = inputRow.getBoundingClientRect();
      const centeredTop =
        scrollParent.scrollTop +
        (targetRect.top - parentRect.top) -
        (scrollParent.clientHeight - targetRect.height) / 2;

      scrollParent.scrollTo({ top: Math.max(0, centeredTop), behavior: 'smooth' });
      return;
    }

    inputRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const highlightPromptInput = useCallback(() => {
    setIsInputHighlighted(true);
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    highlightTimeoutRef.current = setTimeout(() => {
      setIsInputHighlighted(false);
    }, 850);
  }, []);

  useEffect(() => {
    return () => {
      if (focusTimeoutRef.current) clearTimeout(focusTimeoutRef.current);
      if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
    };
  }, []);

  const bubbles = useMemo(
    () => [
      'AiPromptSection.bubble1',
      'AiPromptSection.bubble2',
      'AiPromptSection.bubble3',
      'AiPromptSection.bubble4',
      'AiPromptSection.bubble5',
      'AiPromptSection.bubble6',
      'AiPromptSection.bubble7',
    ],
    []
  );

  const handleBubbleClick = useCallback(
    id => {
      const text = intl.formatMessage({ id });
      setPrompt(text);
      scrollToPromptInput();
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
      }
      focusTimeoutRef.current = setTimeout(() => {
        focusPromptInput();
      }, 260);
      highlightPromptInput();
    },
    [intl, scrollToPromptInput, focusPromptInput, highlightPromptInput]
  );

  const goToAiWithPrompt = useCallback(
    text => {
      const clean = (text || '').trim();
      if (!clean) return;

      const params = new URLSearchParams({ prompt: clean });

      history.push(`/ai?${params.toString()}`);
    },
    [history]
  );
  const handleSend = useCallback(() => {
    goToAiWithPrompt(prompt);
  }, [goToAiWithPrompt, prompt]);
  const isActive = prompt.trim().length > 0;

  const handleChange = e => {
    setPrompt(e.target.value);
  };
  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
  };

  const handleTryForFree = () => {
    // ✅ Update this route if your demo route is different
    history.push('/ai-demo');
  };

  return (
    <section className={`${css.section} ${className || ''}`}>
      <span className={css.shape1}>
        <img src={shape1} alt="" />
      </span>
      <span className={css.shape2}>
        <img src={shape2} alt="" />
      </span>
      <span className={css.shape3}>
        <img src={shape3} alt="" />
      </span>
      <div className={css.inner}>
        <header className={css.header}>
          <h2 className={css.title}>{intl.formatMessage({ id: 'AiPromptSection.title' })}</h2>
          <p className={css.subtitle}>{intl.formatMessage({ id: 'AiPromptSection.subtitle' })}</p>
        </header>

        <div className={css.bubbles}>
          {bubbles.map(id => (
            <button
              key={id}
              type="button"
              className={css.bubble}
              onClick={() => handleBubbleClick(id)}
            >
              {intl.formatMessage({ id })}
            </button>
          ))}
        </div>

        <div
          ref={inputRowRef}
          className={classNames(css.inputRow, isInputHighlighted ? css.inputRowHighlighted : null)}
        >
          {/* <label className={css.srOnly} htmlFor="aiPromptInput">
            {intl.formatMessage({ id: 'AiPromptSection.inputLabel' })}
          </label> */}

          <input
            id="aiPromptInput"
            ref={inputRef}
            className={css.input}
            value={prompt}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={intl.formatMessage({ id: 'AiPromptSection.inputPlaceholder' })}
            inputMode="text"
            autoComplete="off"
            enterkeyhint="send"
          />
          <div className={css.inputButtons}>
            {/*
            <button className={css.microphoneButton} type="button">
              <Icons name="microphone" />
            </button>
          */}

            <button
              type="button"
              className={classNames(isActive ? css.sendBtnActive : null, css.sendButton)}
              onClick={handleSend}
              disabled={!isActive}
            >
              <Icons name="arrowUp" />
            </button>
          </div>
        </div>

        {/* <div className={css.ctaRow}>
          <button type="button" className={css.cta} onClick={handleTryForFree}>
            {intl.formatMessage({ id: 'AiPromptSection.tryForFree' })}
          </button>
        </div> */}
      </div>
      <div className={css.waveImg}>
        <img src={waveImg} alt="" />
      </div>
    </section>
  );
};

AiPromptSection.propTypes = {
  intl: intlShape.isRequired,
  className: PropTypes.string,
};

export default withRouter(injectIntl(AiPromptSection));
